import _ from 'lodash';
import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  ViewChild
} from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { SurveyForm } from 'src/app/shared/models/survey/surveyForm';
import { SurveyService } from 'src/app/shared/services/survey.service';
import { Subscription } from 'rxjs';
import { AttributesEditorDataSource } from 'src/shared-app-portal/components/attributes-editor/attributesEditorDataSource';
import { AttributesEditorComponent } from 'src/shared-app-portal/components/attributes-editor/attributes-editor.component';
import { BuildInfoCheckService } from 'src/app/shared/services/build-info-check.service';
import { AccessibilityService } from 'src/app/shared/services/accessibility.service';
import { ActivatedRoute } from '@angular/router';
import {
  LocalData,
  LocalPersistenceService
} from 'src/shared-app-portal/services/local-persistence.service';
import { Attribute } from 'src/app/shared/models/attributes/attribute';
import {
  FieldValidatorTracker,
  getAllControlErrors
} from 'src/app/shared/models/form/field-validator-tracker';

@Component({
  selector: 'survey-respond',
  templateUrl: './survey-respond.component.html',
  styleUrls: ['./survey-respond.component.scss'],
  standalone: false
})
export class SurveyRespondComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  editorDataSource = new AttributesEditorDataSource();
  isSaving: boolean = false;
  survey: SurveyForm;
  secureLinkId: string;
  formFailedValidations: FieldValidatorTracker[] = [];
  @Input()
  inPreviewMode: boolean = false;
  @Output()
  previewSurveySave = new EventEmitter();
  isHeaderButtonVisible: boolean = false;
  @ViewChild('attributesEditor', { static: false }) attributesEditor: AttributesEditorComponent;

  constructor(
    private route: ActivatedRoute,
    private surveyService: SurveyService,
    private localPersistenceService: LocalPersistenceService,
    private buildInfoCheckService: BuildInfoCheckService,
    private accessibilityService: AccessibilityService
  ) {}

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
  }

  ngOnInit() {
    if (!this.inPreviewMode) {
      this.subscriptions.push(
        this.surveyService.survey.subscribe((result) => {
          if (result) {
            this.surveyService.setSurveyForm();
            this.survey = result;
            let attributes = result.personAndSeatAttributes;
            const localData = this.localPersistenceService.getLocalData<LocalData<null>>(
              this.surveyService.secureLinkId
            );
            let loadedAttributes =
              localData && localData.answers
                ? LocalPersistenceService.buildFormUsingSavedAnswers(
                    localData.answers,
                    this.survey.questions
                  )
                : [];
            attributes = attributes.concat(loadedAttributes);
            this.editorDataSource.sortOnName = false;
            this.editorDataSource.load(
              this.filterForShowCurrentValues(attributes),
              this.survey.questions
            );
            this.setupHeaderButton();
            this.buildInfoCheckService.initBuildFileCheck(
              "Don't worry, you won't lose your progress!"
            );
          }
        }),
        this.buildInfoCheckService.onReloadPrompt.subscribe(() => {
          this.localPersistenceService.saveDataForLater(
            this.buildLocalData(),
            this.surveyService.secureLinkId,
            false
          );
        })
      );
    } else {
      this.subscriptions.push(
        this.surveyService.surveyPreview.subscribe((result) => {
          if (result) {
            this.survey = result;
            this.editorDataSource.sortOnName = false;
            this.editorDataSource.load([], this.survey.questions);
          }
        })
      );
    }
  }

  public filterForShowCurrentValues(values: Array<Attribute>): Array<Attribute> {
    let filtered = _.filter(values, (attribute) => {
      let attributeDefinition = _.find(
        this.survey.questions,
        (attributeDefinition) => attributeDefinition.id == attribute.attributeDefinitionId
      );

      let hiddenSubField =
        attributeDefinition?.uiType == 'Complex' &&
        attributeDefinition.subFieldList.find(
          (sf) => sf.id == attribute.complexAttributeSubFieldValue.subFieldDefinitionItemId
        )?.isHiddenOnForms;

      return attributeDefinition ? !hiddenSubField || attribute.edited : false;
    });
    return filtered;
  }

  isSurveyValid(): boolean {
    return this.editorDataSource.isValid;
  }

  onAttributeBlurred(): void {
    if (this.formFailedValidations.length) {
      this.generateFailedValidationSummary();
    }
  }

  generateFailedValidationSummary(): void {
    const failedValidations: FieldValidatorTracker[] = [];
    const nonValid: Map<string, AbstractControl> = this.attributesEditor?.nonValidAttributes();
    nonValid?.forEach((control: AbstractControl, controlId: string) => {
      failedValidations.push(...getAllControlErrors(controlId, null, control));
    });
    this.formFailedValidations = failedValidations;
  }

  public saveSurvey(): void {
    if (this.isSaving) return;

    if (!this.isSurveyValid()) {
      this.attributesEditor?.markAllAsTouched();
      this.generateFailedValidationSummary();
      return;
    }

    this.formFailedValidations = [];
    this.isSaving = true;
    this.accessibilityService.talk('Submitting...');
    if (this.inPreviewMode) {
      this.previewSurveySave.emit();
      return;
    }
    this.surveyService.setIsLoadingOrSaving(true);
    const { attributes, complexAttributes } = this.editorDataSource.getWriteAttributes();
    const payload = { attributes: attributes, complexAttributes: complexAttributes };
    this.surveyService.updateSurvey(payload);
    this.localPersistenceService.deleteLocalData(this.surveyService.secureLinkId);
  }
  downloadFile(event: { fileName: string; attributeDefinitionId: string }) {
    let secureLinkId = this.route.parent.snapshot?.params['secureLinkId'];

    let requestUrl = `${secureLinkId}/${event.attributeDefinitionId}/document`;
    this.surveyService.downloadFile(requestUrl, event.fileName);
  }
  private setupHeaderButton() {
    if (this.isHeaderButtonVisible) return;
    this.localPersistenceService.buttonSubject.next({
      label: 'Save For Later',
      action: () =>
        this.localPersistenceService.saveDataForLater(
          this.buildLocalData(),
          this.surveyService.secureLinkId
        ),
      tooltip:
        'Your responses will be saved for 15 days. You must use the same device and browser when ' +
        "coming back. You must still submit responses once you're ready."
    });
    this.isHeaderButtonVisible = true;
  }

  private buildLocalData(): LocalData<null> {
    return {
      reference: null,
      answers: this.localPersistenceService.getAnswers(this.editorDataSource)
    };
  }
}
